<template>
  <div class="py-6 px-8">
    <AppointmentListHeader />

    <AppointmentListFilters />

    <div v-if="isAustralianRealEstate" class="d-flex align-center grey lighten-3 px-5 py-5 mb-3 rounded-lg">
      <v-switch
        v-model="showExtraHeaders"
        :label="$t('appointment.table.showExtraFields')"
        class="table-switch my-2"
        hide-details
        dense
        inset
      />
    </div>

    <h-draggable>
      <v-data-table
        @dblclick:row="showAppointmentRow"
        v-model="selectedAppointments"
        :loading-text="$t('appointment.table.loading')"
        :no-data-text="$t('appointment.table.noData')"
        :items="appointments"
        :loading="isLoading"
        :items-per-page="30"
        :headers="headers"
        item-key="id"
        mobile-breakpoint="20"
        class="clickable"
        hide-default-footer
        disable-sort
      >
        <template v-slot:[`item.code`]="{ item }">
          <span class="font-weight-bold">
            {{ item.code }}
          </span>
        </template>
        <template v-slot:[`item.type`]="{ item }">
          {{ $t(`type.${item.type}`) }}
        </template>
        <template v-slot:[`item.realtiesCode`]="{ item }">
          <AppointmentListRealties
            :show-extra-content="showExtraHeaders"
            :realties="item.realties"
          />
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :class="formatColors(item.status)" small>
            {{ $t(`status.${item.status}`) }}
          </v-chip>
        </template>
        <template v-slot:[`item.date`]="{ item }">
          {{ displayContent(timeSlot(item.date, item.slot_size)) }}
        </template>
        <template v-slot:[`item.cancel_date`]="{ item }">
          {{ displayContent(item.cancel_date, 'date') }}
        </template>
        <template v-slot:[`item.group`]="{ item }">
          <span :class="item.group ? 'font-weight-bold' : ''">
            {{ displayContent(item.group && item.group.code) }}
          </span>
        </template>
        <template v-slot:[`item.worker_type`]="{ item }">
          {{ displayContent(item.worker_type, 'workerType') }}
        </template>
        <template v-slot:[`item.host`]="{ item }">
          {{ displayContent(item.host && item.host.name) }}
        </template>
        <template v-slot:[`item.branch`]="{ item }">
          {{ displayContent(item.branch && item.branch.name) }}
        </template>
        <template v-slot:[`item.observations`]="{ item }">
          {{ displayContent(item.observations) }}
        </template>
        <template v-slot:[`item.visitor_name`]="{ item }">
          {{ displayContent(item.visitor_name) }}
        </template>
        <template v-slot:[`item.visitor_phone`]="{ item }">
          {{ displayContent(item.visitor_phone, 'phone') }}
        </template>
        <template v-slot:item.confirmation="{ item }">
          <ShowConfirmedStatus
            :confirmation="item.confirmation"
          />
        </template>
        <template v-slot:[`item.additional_data`]="{ item }">
          <div v-if="item.additional_data" v-for="additionalData in item.additional_data">
            <span class="font-weight-bold caption">
              {{ additionalData.title }}:
            </span>
            <span>
              {{ additionalData.value }}
            </span>
          </div>
          <div v-if="item.cancel_reason">
            <span class="font-weight-bold caption">
              {{ $t('appointment.label.cancelReason') }}:
            </span>
            <span>
              {{ item.cancel_reason }}
            </span>
          </div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <MenuAppointment :appointment="item" />
          </div>
        </template>
      </v-data-table>
    </h-draggable>

    <h-pagination :fetch="fetchAppointments" />

    <RightDrawer :moreInfo="false">
      <AppointmentVisualization :data="details" />
    </RightDrawer>

    <ModalBatchSchedule />
    <MenuAppointmentModals
      @refreshAppointments="refreshAppointments"
      :selectedAppointments="selectedAppointments"
      :isAdmin="false"
    />
  </div>
</template>

<script>
import RightDrawer from '@/components/app/RightDrawer'
import HDraggable from '@/components/app/HDraggable.vue'

import ModalBatchSchedule from '@/components/appointment/modals/ModalBatchSchedule'

import AppointmentVisualization from '@/components/appointment/AppointmentVisualization'
import AppointmentListHeader from '@/components/appointment/fragments/AppointmentListHeader'
import AppointmentListFilters from '@/components/appointment/AppointmentListFilters'
import MenuAppointment from '@/components/appointment/MenuAppointment'
import MenuAppointmentModals from '@/components/appointment/MenuAppointmentModals'
import AppointmentListRealties from '@/components/appointment/fragments/AppointmentListRealties'

import { mapGetters, mapActions, mapMutations } from 'vuex'
import { appointmentHeaders, advancedCustomerAppointmentHeaders } from './listHeaders/appointmentHeaders'
import { timeSlot, displayContent } from '@/util/catalog'
import { handleColors } from '@/util/colors'
import { handleParams } from '@/util/routes'
import { headers } from './listHeaders/batchScheduleHeaders'

import router from '@/router'
import ShowConfirmedStatus from './mapDrawing/fragments/ShowConfirmedStatus.vue'

export default {
  name: 'AppointmentList',

  components: {
    AppointmentListFilters,
    AppointmentVisualization,
    AppointmentListHeader,
    AppointmentListRealties,
    RightDrawer,
    ModalBatchSchedule,
    HDraggable,
    MenuAppointment,
    MenuAppointmentModals,
    ShowConfirmedStatus,
  },

  computed: {
    ...mapGetters(['isLoading', 'appointment', 'appointments']),

    isAustralianRealEstate() {
      return localStorage.getItem('user_country') == 'AU'
    },

    headers() {
      return this.showExtraHeaders ? advancedCustomerAppointmentHeaders : appointmentHeaders
    }
  },

  methods: {
    ...mapActions(['fetchAppointments', 'fetchAppointment', 'openModal']),

    ...mapMutations(['setIsDrawerOpen']),

    formatColors: handleColors,

    showAppointment(id) {
      this.setIsDrawerOpen(true)
      this.fetchAppointment(id).then(() => (this.details = this.appointment))

      handleParams('appointments', id)
    },

    showAppointmentRow(event, { item }) {
      this.showAppointment(item?.id)
    },

    refreshAppointments() {
      this.fetchAppointments()
    },
  },

  data() {
    return {
      timeSlot,
      displayContent,
      details: {},
      showExtraHeaders: false,
      selectedAppointments: [],
    }
  },

  mounted() {
    const appointmentId = router.currentRoute.query.id
    appointmentId && this.showAppointment(appointmentId)
    this.showExtraHeaders = this.isAustralianRealEstate
  },
}
</script>
